import React from 'react';

import { useFullStoryPageView } from 'hooks/useFullstoryPageView';

import { useBattleCardsStore } from 'store/battlecards.store';

import { cardsDataQueryKey } from 'api/queries/cardsData.query';
import { listCardsDataQueryKey } from 'api/queries/getListFilteredCards.query';

import { GlobalFilterButton } from 'components/common/GlobalFilterButton/GlobalFilterButton';
import { GlobalFiltersInPage } from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage';
import { ActivityCodesFilter } from 'components/common/GlobalFiltersInPage/components/ActivityCodesFilter/ActivityCodesFilter';
import { ActivityForwardFragnetFilter } from 'components/common/GlobalFiltersInPage/components/ActivityForwardFragnetFilter/ActivityForwardFragnetFilter';
import { Assignee } from 'components/common/GlobalFiltersInPage/components/Assignee/Assignee';
import { CardActivityFilter } from 'components/common/GlobalFiltersInPage/components/CardActivityFilter/CardActivityFilter';
import { CompletionPlanSegmentFilter } from 'components/common/GlobalFiltersInPage/components/CompletionPlanSegmentFilter/CompletionPlanSegmentFilter';
import { ImportantFilter } from 'components/common/GlobalFiltersInPage/components/ImportantFilter/ImportantFilter';
import { MilestoneFragnetFilter } from 'components/common/GlobalFiltersInPage/components/MilestoneFragnetFilter/MilestoneFragnetFilter';
import { StatusFilter } from 'components/common/GlobalFiltersInPage/components/StatusFilter/StatusFilter';
import { TagsFilter } from 'components/common/GlobalFiltersInPage/components/TagsFilter/TagsFilter';
import { UrgentFilter } from 'components/common/GlobalFiltersInPage/components/UrgentFilter/UrgentFilter';
import { WbsFilter } from 'components/common/GlobalFiltersInPage/components/WbsFilter/wbsFilter';
import { BaselineEndDateFilter } from 'components/common/GlobalFiltersInPage/components/baselineEndDateFilter/BaselineEndDateFilter';
import { BaselineStartDateFilter } from 'components/common/GlobalFiltersInPage/components/baselineStartDateFilter/BaselineStartDateFilter';
import { CardTypeFilter } from 'components/common/GlobalFiltersInPage/components/cardTypeFilters/CardTypeFilters';
import { ClusterFilter } from 'components/common/GlobalFiltersInPage/components/clusterFilter/ClusterFilter';
import { DurationFilter } from 'components/common/GlobalFiltersInPage/components/durationFilter/DurationFilter';
import { FinishDateFilter } from 'components/common/GlobalFiltersInPage/components/finishDateFilter/FinishDateFilter';
import { FloatFilter } from 'components/common/GlobalFiltersInPage/components/floatFilter/FloatFilter';
import { HasAttachmentsFilter } from 'components/common/GlobalFiltersInPage/components/hasAttachmentsFilter/HasAttachmentsFilter';
import { HasCommentsFilter } from 'components/common/GlobalFiltersInPage/components/hasCommentsFilter/HasCommentsFilter';
import { LagLeadFilter } from 'components/common/GlobalFiltersInPage/components/lagLeadFilter/LagLeadFilter';
import { RelatedActivityFilter } from 'components/common/GlobalFiltersInPage/components/relatedActivityFilter/RelatedActivityFilter';
import { ResourceNameFilter } from 'components/common/GlobalFiltersInPage/components/resourceNameFilter/ResourceNameFilter';
import { ResourceTypeFilter } from 'components/common/GlobalFiltersInPage/components/resourceTypeFilter/ResourceTypeFilter';
import { RiskIndicatorsFilter } from 'components/common/GlobalFiltersInPage/components/riskIndicatorsFilter/RiskIndicatorsFilter';
import { SearchByNameFilter } from 'components/common/GlobalFiltersInPage/components/searchByNameFilter/SearchByNameFilter';
import { StartDateFilter } from 'components/common/GlobalFiltersInPage/components/startDateFilter/StartDateFilter';
import { SubmittedCompletionFilter } from 'components/common/GlobalFiltersInPage/components/submitedCompletionFilter/SubmitedCompletionFilter';
import { TrackedActivity } from 'components/common/GlobalFiltersInPage/components/trackedActivityFilter/TrackedActivityFilter';
import { UpdatedByFilter } from 'components/common/GlobalFiltersInPage/components/updatedByFilter/UpdatedByFilter';
import { WbsLevelFilter } from 'components/common/GlobalFiltersInPage/components/wbsLevelFilter/WbsLevelFilter';
import { GlobalFilterFieldKey } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import { MainCardRoutingModal } from 'components/common/MainCardRoutingModal/mainCardRoutingModal';
import { BottomHeaderSection } from 'components/common/PageContainer/BottomHeaderSection';
import { HeaderWrapper } from 'components/common/PageContainer/HeaderWrapper';
import { MainContent } from 'components/common/PageContainer/MainContent';
import PageContainer from 'components/common/PageContainer/PageContainer';
import { VersionSection } from 'components/common/PageContainer/versionSection';
import { VersionWrapper } from 'components/common/PageContainer/versionWrapper';
import { PageWidgetWrapper } from 'components/common/PageWidgetWrapper/PageWidgetWrapper';
import { ProgramTopHeader } from 'components/common/ProgramTopHeader/ProgramTopHeader';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import { ToggleView } from 'components/common/ToggleView/ToggleView';

import { AddTaskWrapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/AddTaskWrapper/AddTaskWrapper';
import { ColumnsBoardWrapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/ColumnsBoard/ColumnsBoardWrapper';
import { PivotBoard } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/PivotBoard';
import { RowsBoard } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/RowsBoard';
import { BulkAssignWrapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/bulkAssign/bulkAssignWrapper';
import { ActivitiesSegments } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/components/ActivitiesSegments/ActivitiesSegments';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { Button, TreeProgramTopHeader } from 'components/common';

export const ProjectActivitiesPage = () => {
    useFullStoryPageView({ pageName: executionConfig.battlecards.title });
    const displayStyle = useBattleCardsStore((state) => state.displayStyle);
    const { setDisplayStyle } = useBattleCardsStore();

    return (
        <PageContainer>
            <HeaderWrapper>
                <VersionWrapper>
                    <VersionSection>
                        <TreeProgramTopHeader versionSelectorEnabled={true}>
                            <ShareInsightButton
                                title={executionConfig.battlecards.title}
                                link={window.location.href}
                                customTriggerElement={
                                    <Button variant={'secondary'} circle>
                                        <MuiIcon icon={'share'} />
                                    </Button>
                                }
                            />
                            <AddTaskWrapper />
                        </TreeProgramTopHeader>
                    </VersionSection>
                    <GlobalFilterButton />
                </VersionWrapper>
                <BottomHeaderSection>
                    <ProgramTopHeader
                        pageTitle={executionConfig.battlecards.title}
                        positionRightComponent={
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <ActivitiesSegments />
                                {/*<Separator className={classes.separator} />*/}
                                <BulkAssignWrapper />
                                <ToggleView displayStyle={displayStyle} setDisplayStyle={setDisplayStyle} />
                            </div>
                        }
                    />
                    {/*<FiltersWrapper />*/}
                    <GlobalFiltersInPage
                        defaultValues={{
                            [GlobalFilterFieldKey.cardActivity]: ['TASK_DEPENDENT', 'START_MILESTONE'],
                            [GlobalFilterFieldKey.assignee]: null,
                            [GlobalFilterFieldKey.status]: null,
                            [GlobalFilterFieldKey.milestoneFragnet]: null,
                            [GlobalFilterFieldKey.tags]: null,
                            [GlobalFilterFieldKey.activityCodes]: null,
                            [GlobalFilterFieldKey.wbs]: null,
                            [GlobalFilterFieldKey.important]: null,
                            [GlobalFilterFieldKey.urgent]: null,
                            [GlobalFilterFieldKey.completionPlanSegment]: null,
                            [GlobalFilterFieldKey.hasAttachments]: null,
                            [GlobalFilterFieldKey.lagLead]: null,
                            [GlobalFilterFieldKey.trackedActivity]: null,
                            [GlobalFilterFieldKey.activityForwardFragnet]: null,
                            [GlobalFilterFieldKey.cardType]: null,
                            [GlobalFilterFieldKey.finishDate]: null,
                            [GlobalFilterFieldKey.cluster]: null,
                            [GlobalFilterFieldKey.duration]: null,
                            [GlobalFilterFieldKey.float]: null,
                            [GlobalFilterFieldKey.startDate]: null,
                            [GlobalFilterFieldKey.baselineStartDate]: null,
                            [GlobalFilterFieldKey.baselineFinishDate]: null,
                            [GlobalFilterFieldKey.hasComments]: null,
                            [GlobalFilterFieldKey.relatedActivity]: null,
                            [GlobalFilterFieldKey.resourceName]: null,
                            [GlobalFilterFieldKey.resourceType]: null,
                            [GlobalFilterFieldKey.submittedCompletion]: null,
                            [GlobalFilterFieldKey.updatedBy]: null,
                            [GlobalFilterFieldKey.wbsLevel]: null,
                            [GlobalFilterFieldKey.riskIndicators]: null,
                            [GlobalFilterFieldKey.searchByName]: null,
                        }}
                        //TODO: Find better solution then clearFiltersValue props
                        clearFiltersValue={{
                            [GlobalFilterFieldKey.cardActivity]: null,
                            [GlobalFilterFieldKey.assignee]: null,
                            [GlobalFilterFieldKey.status]: null,
                            [GlobalFilterFieldKey.milestoneFragnet]: null,
                            [GlobalFilterFieldKey.tags]: null,
                            [GlobalFilterFieldKey.activityCodes]: null,
                            [GlobalFilterFieldKey.wbs]: null,
                            [GlobalFilterFieldKey.important]: null,
                            [GlobalFilterFieldKey.urgent]: null,
                            [GlobalFilterFieldKey.completionPlanSegment]: null,
                            [GlobalFilterFieldKey.hasAttachments]: null,
                            [GlobalFilterFieldKey.lagLead]: null,
                            [GlobalFilterFieldKey.trackedActivity]: null,
                            [GlobalFilterFieldKey.activityForwardFragnet]: null,
                            [GlobalFilterFieldKey.cardType]: null,
                            [GlobalFilterFieldKey.finishDate]: null,
                            [GlobalFilterFieldKey.cluster]: null,
                            [GlobalFilterFieldKey.duration]: null,
                            [GlobalFilterFieldKey.float]: null,
                            [GlobalFilterFieldKey.startDate]: null,
                            [GlobalFilterFieldKey.baselineStartDate]: null,
                            [GlobalFilterFieldKey.baselineFinishDate]: null,
                            [GlobalFilterFieldKey.hasComments]: null,
                            [GlobalFilterFieldKey.relatedActivity]: null,
                            [GlobalFilterFieldKey.resourceName]: null,
                            [GlobalFilterFieldKey.resourceType]: null,
                            [GlobalFilterFieldKey.submittedCompletion]: null,
                            [GlobalFilterFieldKey.updatedBy]: null,
                            [GlobalFilterFieldKey.wbsLevel]: null,
                            [GlobalFilterFieldKey.riskIndicators]: null,
                            [GlobalFilterFieldKey.searchByName]: null,
                        }}
                        dropdownFiltersKeys={[
                            GlobalFilterFieldKey.milestoneFragnet,
                            GlobalFilterFieldKey.activityForwardFragnet,
                            GlobalFilterFieldKey.baselineStartDate,
                            GlobalFilterFieldKey.baselineFinishDate,
                            GlobalFilterFieldKey.cardType,
                            GlobalFilterFieldKey.cluster,
                            GlobalFilterFieldKey.completionPlanSegment,
                            GlobalFilterFieldKey.duration,
                            GlobalFilterFieldKey.float,
                            GlobalFilterFieldKey.hasAttachments,
                            GlobalFilterFieldKey.hasComments,
                            GlobalFilterFieldKey.lagLead,
                            GlobalFilterFieldKey.relatedActivity,
                            GlobalFilterFieldKey.resourceName,
                            GlobalFilterFieldKey.resourceType,
                            GlobalFilterFieldKey.startDate,
                            GlobalFilterFieldKey.submittedCompletion,
                            GlobalFilterFieldKey.trackedActivity,
                            GlobalFilterFieldKey.updatedBy,
                            GlobalFilterFieldKey.wbsLevel,
                        ]}
                    >
                        <CardActivityFilter name={GlobalFilterFieldKey.cardActivity} />
                        <Assignee name={GlobalFilterFieldKey.assignee} />
                        <StatusFilter name={GlobalFilterFieldKey.status} />
                        <FinishDateFilter name={GlobalFilterFieldKey.finishDate} />
                        <RiskIndicatorsFilter name={GlobalFilterFieldKey.riskIndicators} />
                        <ImportantFilter name={GlobalFilterFieldKey.important} />
                        <UrgentFilter name={GlobalFilterFieldKey.urgent} />
                        <TagsFilter name={GlobalFilterFieldKey.tags} />
                        <ActivityCodesFilter name={GlobalFilterFieldKey.activityCodes} />
                        <WbsFilter name={GlobalFilterFieldKey.wbs} />
                        <SearchByNameFilter name={GlobalFilterFieldKey.searchByName} />
                        <BaselineStartDateFilter name={GlobalFilterFieldKey.baselineStartDate} />
                        <BaselineEndDateFilter name={GlobalFilterFieldKey.baselineFinishDate} />
                        <MilestoneFragnetFilter name={GlobalFilterFieldKey.milestoneFragnet} />
                        <CompletionPlanSegmentFilter name={GlobalFilterFieldKey.completionPlanSegment} />
                        <HasAttachmentsFilter name={GlobalFilterFieldKey.hasAttachments} />
                        <LagLeadFilter name={GlobalFilterFieldKey.lagLead} />
                        <TrackedActivity name={GlobalFilterFieldKey.trackedActivity} />
                        <ActivityForwardFragnetFilter name={GlobalFilterFieldKey.activityForwardFragnet} />
                        <CardTypeFilter name={GlobalFilterFieldKey.cardType} />
                        <ClusterFilter name={GlobalFilterFieldKey.cluster} />
                        <DurationFilter name={GlobalFilterFieldKey.duration} />
                        <FloatFilter name={GlobalFilterFieldKey.float} />
                        <StartDateFilter name={GlobalFilterFieldKey.startDate} />
                        <HasCommentsFilter name={GlobalFilterFieldKey.hasComments} />
                        <RelatedActivityFilter name={GlobalFilterFieldKey.relatedActivity} />
                        <ResourceNameFilter name={GlobalFilterFieldKey.resourceName} />
                        <ResourceTypeFilter name={GlobalFilterFieldKey.resourceType} />
                        <SubmittedCompletionFilter name={GlobalFilterFieldKey.submittedCompletion} />
                        <UpdatedByFilter name={GlobalFilterFieldKey.updatedBy} />
                        <WbsLevelFilter name={GlobalFilterFieldKey.wbsLevel} />
                    </GlobalFiltersInPage>
                </BottomHeaderSection>
            </HeaderWrapper>

            <MainContent>
                <PageWidgetWrapper>
                    <ColumnsBoardWrapper />
                    <RowsBoard />
                    <PivotBoard />
                </PageWidgetWrapper>
            </MainContent>
            <MainCardRoutingModal queryKeys={[cardsDataQueryKey, listCardsDataQueryKey, 'CountersData', 'boardInit']} />
        </PageContainer>
    );
};
